import { GetHierarchyQuery } from '@modules/search';
import { HierarchyNode, NodeContent, PrototypeGlDataValue } from '@src/__generated__/types';
import { ShortListNode } from '@src/_new/modules/short-list/contexts/ShortList.types';
import { NodeData } from '@src/components/hierarchies/hooks/useTransformEditNodeData';
import { isFinite } from 'lodash';

import { LongListEntry } from '../components/PrototypeMenu/PrototypeLongList/longLists/context/LongListsContext/LongListsContext';
import {
    FormState,
    PriceTotals,
    Prototype,
    PrototypeGlsLink,
    PrototypeValues,
    Values,
} from '../types';

export function transformData(inputObject: PriceTotals) {
    const resultObject: PriceTotals = {};
    const keys = Object.keys(Object.values(inputObject)[0] ?? {});

    keys.forEach(key => {
        Object.entries(inputObject).forEach(([priceKey, prototypeTotals]) => {
            if (!resultObject[key]) {
                resultObject[key] = {};
            }
            resultObject[key] = { ...resultObject[key], [priceKey]: prototypeTotals[key] };
        });
    });

    return resultObject;
}

export const finiteNumber = (n: number, isPercent = false) =>
    isFinite(n) ? parseFloat(n.toFixed(isPercent ? 4 : 2)) : 0;

interface NodeWithContents {
    contents: { contentType: string; value?: { value?: string[] }[] }[];
}

function hasContents(node: any): node is NodeWithContents {
    return Array.isArray(node?.contents);
}

export function getValue<T extends boolean = true>(
    node:
        | Omit<GetHierarchyQuery['searchHierarchies']['nodes']['edges'][number]['node'], 'disabled'>
        | LongListEntry['node']
        | ShortListNode
        | HierarchyNode
        | Prototype
        | NodeData
        | undefined,
    contentType: string,
    placeholder: T = true as T,
): T extends true ? string : string | null {
    if (!node || !hasContents(node)) {
        return (placeholder ? '-' : null) as T extends true ? string : string | null;
    }

    const foundContent = node.contents.find(content => content.contentType === contentType);

    if (!foundContent) {
        return (placeholder ? '-' : null) as T extends true ? string : string | null;
    }

    return (foundContent.value?.[0]?.value?.[0] ?? '') as T extends true ? string : string | null;
}

/**
 * The cash discount uplift of a GL, as the ratio between its own `dnCashD` and its `dn`, mirroring
 * `cashDiscountRatio` in `server/src/CGT/jobs/AlignPrices/prototype.helpers.ts`.
 *
 * A GL builds `dnCashD` item by item, off each item's own cash-discount flag, so the ratio carries
 * that composition; the flag on the model primary price list — the only other source — knows nothing
 * about the items and can disagree with them, which is what used to leave a prototype LC below the
 * LC of the GL it prices. The price list flag stays as the fallback for a GL with no DN to read a
 * ratio from.
 */
export function cashDiscountRatio(dn: number, dnCashD: number, cashDiscount = 0): number {
    if (dn && dnCashD) return dnCashD / dn;
    return 1 / (1 - cashDiscount);
}

/**
 * The transport charge a prototype forces on one of its GL selections, mirroring
 * `resolveForcedTransportCosts` in `server/src/CGT/jobs/AlignPrices/prototype.helpers.ts`.
 *
 * `forcedTransportCosts` is the explicit marker, written from the moment the user forces the value.
 * Selections saved before the marker existed only carry the effective `transport_costs`, which the
 * prototype save writes on every selection: it counts as a forcing only when it is non-zero and
 * differs from the GL charge — an equal value was just the inherited one, and a zero one already
 * fell back to the GL charge.
 */
export function resolveForcedTransportCosts(params: {
    forcedTransportCosts?: number | string | null;
    transportCosts?: number | string | null;
    glTransportCosts?: number | string | null;
}): number | undefined {
    const toNumber = (value: number | string | null | undefined) => {
        if (value === null || value === undefined || value === '') return null;
        const parsed = Number(value);
        return isFinite(parsed) ? parsed : null;
    };

    const marker = toNumber(params.forcedTransportCosts);
    if (marker !== null) return marker;

    const stored = toNumber(params.transportCosts);
    if (stored === null || stored === 0) return undefined;

    const gl = toNumber(params.glTransportCosts) ?? 0;
    return Math.abs(stored - gl) < 0.005 ? undefined : stored;
}

// Persisted GL selection values follow the AlignPrices server conventions
// (server AlignPrices prototype.helpers): `dn` is the delta-adjusted dnWithDelta and
// GP values are fractions (0.15). The live panel values keep the raw dn and the
// percent-scaled GPs for display, so they must go through this conversion when saved.
export function toPersistedGlValue(value: Values): Values {
    return {
        ...value,
        dn: value.dnWithDelta ?? value.dn,
        gp: finiteNumber((value.gp ?? 0) / 100),
        gpWithSupports: finiteNumber((value.gpWithSupports ?? 0) / 100),
        dealer_gp: finiteNumber((value.dealer_gp ?? 0) / 100),
        dealerGpWithSupports: finiteNumber((value.dealerGpWithSupports ?? 0) / 100),
    };
}

// Builds the `base_info / selezioni` link values from the live panel state. Shared by the prototype
// save and by the draft export so the two cannot drift apart.
export function buildPrototypeSelections(
    prototypeValue: PrototypeValues[string],
    prototypeFormValue: FormState[string],
): PrototypeGlsLink[] {
    return Object.keys(prototypeValue).map<PrototypeGlsLink>(key => ({
        type: 'link',
        data: {
            link: key,
            value: {
                ...toPersistedGlValue(prototypeValue[key]),
                selection: prototypeFormValue?.[key]?.selection,
                glCode: prototypeFormValue?.[key]?.glCode,
                code: prototypeFormValue?.[key]?.code,
            },
        },
    }));
}

/**
 * Contents the draft export overlays on the stored prototype node, in the same shapes the
 * save paths write. Only prototype-level values are included: model-level supports (`cat_support`)
 * are read from the CGT model node by the exporter, so saving them on the prototype would not change
 * the file either — leaving them out keeps draft and saved exports comparable.
 */
export function buildDraftPrototypeContents(params: {
    selections: PrototypeGlsLink[];
    recap: Record<string, number> | undefined;
    cgtSupport: Record<string, string> | undefined;
    defaultLanguage: string;
}): NodeContent[] {
    const { selections, recap, cgtSupport, defaultLanguage } = params;

    const selectionContent: NodeContent = {
        contentType: 'selezioni',
        contentTypeGroup: 'base_info',
        contentTypeGroupIndex: 0,
        repeatable: false,
        type: 'link',
        value: [{ languageIsoCode: defaultLanguage, value: selections }],
    };

    const numberContent = (
        contentTypeGroup: string,
        contentType: string,
        value: string,
    ): NodeContent => ({
        contentType,
        contentTypeGroup,
        contentTypeGroupIndex: 0,
        repeatable: false,
        type: 'number',
        value: [{ languageIsoCode: defaultLanguage, value: [value] }],
    });

    const pricingContents = Object.entries(recap ?? {})
        .filter(([, priceValue]) => priceValue !== null && priceValue !== undefined)
        .map(([priceKey, priceValue]) => numberContent('pricing', priceKey, priceValue.toString()));

    const cgtSupportContents = Object.entries(cgtSupport ?? {})
        .filter(([, supportValue]) => supportValue !== null && supportValue !== undefined)
        .map(([supportKey, supportValue]) =>
            numberContent('cgt_support', supportKey, supportValue.toString()),
        );

    return [selectionContent, ...pricingContents, ...cgtSupportContents];
}

export function convertToPrototypeContents(
    prototypeForm: Record<string, PrototypeGlDataValue>,
    priceTotals: Record<string, number>,
    cgtModelId: string,
    defaultLanguage: string,
) {
    const selectedGls = Object.entries(prototypeForm).reduce<
        { data: { link: string; value: PrototypeGlDataValue }; type: 'link' }[]
    >((acc, [key, glValue]) => {
        acc.push({ data: { link: key, value: glValue }, type: 'link' });
        return acc;
    }, []);

    const selectionContent: NodeContent = {
        contentType: 'selezioni',
        contentTypeGroup: 'base_info',
        contentTypeGroupIndex: 0,
        repeatable: false,
        type: 'link',
        value: [
            {
                languageIsoCode: defaultLanguage,
                value: selectedGls,
            },
        ],
    };

    const pricingContents = Object.entries(priceTotals).reduce<NodeContent[]>(
        (acc, [key, priceValue]) => {
            const content: NodeContent = {
                contentType: key,
                contentTypeGroup: 'pricing',
                contentTypeGroupIndex: 0,
                repeatable: false,
                type: 'number',
                value: [{ languageIsoCode: defaultLanguage, value: [priceValue.toString()] }],
            };
            acc.push(content);
            return acc;
        },
        [],
    );

    const linkedCgtModelContent: NodeContent = {
        contentType: 'linked_cgt_model',
        contentTypeGroup: 'base_info',
        contentTypeGroupIndex: 0,
        repeatable: false,
        type: 'link',
        value: [{ value: [cgtModelId], languageIsoCode: defaultLanguage }],
    };

    return [...pricingContents, selectionContent, linkedCgtModelContent];
}
